import type { AxiosCollection } from '@/types/Components/Response'
import type { TaxNumber } from '@/types/Models/TaxNumber'
import { notify } from '@kyvg/vue3-notification'
import { defineStore } from 'pinia'
import { SenderAddressTaxNumbersProxy } from '@/proxies/Customer/TaxNumbersProxy'
import { none } from '@/services/Helpers'

export const useSenderAddressTaxNumbersStore = defineStore('customers/senderAddressTaxNumbers', {
  state: () => ({
    taxNumbers: [] as TaxNumber[],
    localTaxNumbers: [] as TaxNumber[],
    uiFlags: {} as Record<string, { _saving?: boolean; _deleting?: boolean; _isNew?: boolean }>,
  }),
  getters: {
    taxNumbersWithUi: (state) => state.localTaxNumbers,
    deletingMap: (state) => {
      const m: Record<string, boolean> = {}
      Object.keys(state.uiFlags || {}).forEach((k) => {
        m[k] = !!state.uiFlags[k]?._deleting
      })
      return m
    },
  },
  actions: {
    reset() {
      this.taxNumbers = []
      this.localTaxNumbers = []
      this.uiFlags = {}
    },
    fetch(companyId: string, senderAddressId: string) {
      return new SenderAddressTaxNumbersProxy(companyId, senderAddressId)
        .all()
        .then((response: AxiosCollection<TaxNumber>) => {
          this.taxNumbers = response.data
          this.localTaxNumbers = this.taxNumbers.map((t) => ({ ...t }))
          return response
        })
        .catch(none)
    },
    addLocal() {
      const tempId = `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
      const local: TaxNumber = {
        id: tempId,
        companyId: undefined,
        countryCode: '',
        taxNumber: '',
        createdAt: null,
        updatedAt: null,
      }
      this.localTaxNumbers.push(local)
      this.uiFlags[tempId] = { _isNew: true, _saving: false, _deleting: false }
      return local
    },
    removeLocal(id?: string) {
      if (!id) return
      const idx = this.localTaxNumbers.findIndex((x) => x.id === id)
      if (idx >= 0) this.localTaxNumbers.splice(idx, 1)
      delete this.uiFlags[id]
    },
    async saveAll(companyId: string, senderAddressId: string) {
      const proxy = new SenderAddressTaxNumbersProxy(companyId, senderAddressId)

      for (const tn of this.localTaxNumbers) {
        const hasValue = !!(tn.taxNumber && tn.taxNumber.toString().trim().length > 0)
        if (!hasValue) continue

        const payload = { countryCode: tn.countryCode || '', taxNumber: tn.taxNumber || '' }
        const id = tn.id as string | undefined

        if (id && !id.startsWith('tmp_')) {
          await proxy.update(id, payload).catch(none)
        } else {
          await proxy.create(payload).catch(none)
        }
      }
      await this.fetch(companyId, senderAddressId)
      notify({ group: 'success', text: 'Tax numbers saved' })
    },
    async deleteTaxNumber(companyId: string, senderAddressId: string, id?: string) {
      if (!id) return
      const isTemp = String(id).startsWith('tmp_')
      if (isTemp) {
        this.removeLocal(id)
        return
      }
      this.uiFlags[id] = { ...(this.uiFlags[id] || {}), _deleting: true }
      try {
        await new SenderAddressTaxNumbersProxy(companyId, senderAddressId).destroy(id)
        await this.fetch(companyId, senderAddressId)
        notify({ group: 'success', text: 'Tax number deleted' })
      } catch (e: any) {
        this.uiFlags[id] = { ...(this.uiFlags[id] || {}), _deleting: false }
        notify({ group: 'error', text: e?.message || 'Error deleting tax number' })
      }
    },
  },
})

